import { themeVariables } from '@noths/polaris-design-system-abstract-theme';
import { createGlobalStyle } from 'styled-components';

import { palette } from './primitives/palette';
import { fontFaces } from './styles/fontFaces';

export const GlobalStyles = createGlobalStyle`
  /* font face definitions */
  ${fontFaces};

  /* resets */
  body {
    margin: 0;
    padding: 0;
  }

  /* theme CSS variables */
  :root {
    ${themeVariables.borderRadius.full.desktop}: 9999px;
    ${themeVariables.borderRadius.full.mobile}: 9999px;
    ${themeVariables.borderRadius.md.desktop}: 8px;
    ${themeVariables.borderRadius.md.mobile}: 6px;

    ${themeVariables.colors.content.brand}: ${palette.deepPurpleBase};
    ${themeVariables.colors.content.disabled}: ${palette.grey50};
    ${themeVariables.colors.content.inverse}: ${palette.neutralWhite};
    ${themeVariables.colors.content.negative}: ${palette.redBase};
    ${themeVariables.colors.content.notice}: ${palette.blueBase};
    ${themeVariables.colors.content.positive}: ${palette.greenBase};
    ${themeVariables.colors.content.primary}: ${palette.deepPurpleShade30};
    ${themeVariables.colors.content.secondary}: ${palette.neutralBrandBlack};
    ${themeVariables.colors.content.tertiary}: ${palette.greyBase};

    ${themeVariables.colors.interaction.link.default}: ${palette.purpleBase};
    ${themeVariables.colors.interaction.link.focusVisible}: ${palette.blueBase};
    ${themeVariables.colors.interaction.link.hover}: ${palette.purple70};
    ${themeVariables.colors.interaction.link.pressed}: ${palette.deepPurpleShade20};
    ${themeVariables.colors.interaction.link.visited}: ${palette.deepPurple70};

    ${themeVariables.colors.stroke.brand}: ${palette.deepPurpleBase};
    ${themeVariables.colors.stroke.default}: ${palette.grey30};
    ${themeVariables.colors.stroke.disabled}: ${palette.grey10};
    ${themeVariables.colors.stroke.focus}: ${palette.blueBase};
    ${themeVariables.colors.stroke.negative}: ${palette.redBase};
    ${themeVariables.colors.stroke.notice}: ${palette.yellowBase};
    ${themeVariables.colors.stroke.positive}: ${palette.greenBase};

    ${themeVariables.colors.surface.brand}: ${palette.deepPurpleBase};
    ${themeVariables.colors.surface.default}: ${palette.neutralOffWhite};
    ${themeVariables.colors.surface.disabled}: ${palette.grey5};
    ${themeVariables.colors.surface.inverse}: ${palette.deepPurpleShade30};
    ${themeVariables.colors.surface.negative}: ${palette.red10};
    ${themeVariables.colors.surface.notice}: ${palette.yellow10};
    ${themeVariables.colors.surface.overlay}: rgba(14, 14, 17, 0.6);
    ${themeVariables.colors.surface.positive}: ${palette.green10};

    ${themeVariables.spacing.xs}: 4px;
    ${themeVariables.spacing.sm}: 8px;
    ${themeVariables.spacing.md}: 16px;
    ${themeVariables.spacing.lg}: 24px;
    ${themeVariables.spacing.xl}: 32px;
    ${themeVariables.spacing.xxl}: 40px;

    ${themeVariables.strokeWidth}: 1px;

    ${themeVariables.typography.heading.fontFamily}: 'NOTHS Suisse Neue', 'Lato',
      Arial, sans-serif;
    ${themeVariables.typography.heading.fontWeight}: 500;
    ${themeVariables.typography.heading.letterSpacing}: -4%;

    ${themeVariables.typography.heading.sizes.xs.fontSize}: 18px;
    ${themeVariables.typography.heading.sizes.xs.lineHeight}: 24px;
    ${themeVariables.typography.heading.sizes.sm.fontSize}: 20px; 
    ${themeVariables.typography.heading.sizes.sm.lineHeight}: 28px;
    ${themeVariables.typography.heading.sizes.md.fontSize}: 24px;
    ${themeVariables.typography.heading.sizes.md.lineHeight}: 32px;
    ${themeVariables.typography.heading.sizes.lg.fontSize}: 30px;
    ${themeVariables.typography.heading.sizes.lg.lineHeight}: 38px;
    ${themeVariables.typography.heading.sizes.xl.fontSize}: 36px;
    ${themeVariables.typography.heading.sizes.xl.lineHeight}: 44px;
    ${themeVariables.typography.heading.sizes.xxl.fontSize}: 48px;
    ${themeVariables.typography.heading.sizes.xxl.lineHeight}: 56px;

    ${themeVariables.typography.body.fontFamily}: 'Lato', Arial, sans-serif;
    ${themeVariables.typography.body.fontWeight}: 400;
    ${themeVariables.typography.body.letterSpacing}: 1%;

    ${themeVariables.typography.body.sizes.sm.fontSize}: 13px;
    ${themeVariables.typography.body.sizes.sm.lineHeight}: 18px;
    ${themeVariables.typography.body.sizes.md.fontSize}: 15px;
    ${themeVariables.typography.body.sizes.md.lineHeight}: 20px;
    ${themeVariables.typography.body.sizes.lg.fontSize}: 17px;
    ${themeVariables.typography.body.sizes.lg.lineHeight}: 24px;
  }

  /* base styles */
  body {
    -webkit-font-smoothing: antialiased;
    font-family: var(${themeVariables.typography.body.fontFamily});
    font-weight: var(${themeVariables.typography.body.fontWeight});
    letter-spacing: var(${themeVariables.typography.body.letterSpacing});
    background-color: var(${themeVariables.colors.surface.default});
  }

  *, *:before, *:after {
    box-sizing: border-box;
  }
  
  h1, h2, h3, h4, h5, h6 {
    font-family: var(${themeVariables.typography.heading.fontFamily});
    font-weight: var(${themeVariables.typography.heading.fontWeight});
    letter-spacing: var(${themeVariables.typography.heading.letterSpacing});
  }
  
  h1 {
    font-size: var(${themeVariables.typography.heading.sizes.xxl.fontSize});
    line-height: var(${themeVariables.typography.heading.sizes.xxl.lineHeight});
  }
  
  h2 {
    font-size: var(${themeVariables.typography.heading.sizes.xl.fontSize});
    line-height: var(${themeVariables.typography.heading.sizes.xl.lineHeight});
  }

  h3 {
    font-size: var(${themeVariables.typography.heading.sizes.lg.fontSize});
    line-height: var(${themeVariables.typography.heading.sizes.lg.lineHeight});
  }
  
  h4 {
    font-size: var(${themeVariables.typography.heading.sizes.md.fontSize});
    line-height: var(${themeVariables.typography.heading.sizes.md.lineHeight});
  }
  
  h5 {
    font-size: var(${themeVariables.typography.heading.sizes.sm.fontSize});
    line-height: var(${themeVariables.typography.heading.sizes.sm.lineHeight});
  }

  h6 {
    font-size: var(${themeVariables.typography.heading.sizes.xs.fontSize});
    line-height: var(${themeVariables.typography.heading.sizes.xs.lineHeight});
  }
  
  p {
    font-size: var(${themeVariables.typography.body.sizes.lg.fontSize});
    line-height: var(${themeVariables.typography.body.sizes.lg.lineHeight});
  }
  
  a {
    color: var(${themeVariables.colors.interaction.link.default});
  }
`;
